import { useEffect, useState } from "react";
import { Box, Chip, List, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import type { HeadToHead, MatchOutcome } from "../api/types";
import { calendarService, participantService } from "../api/service.ts";
import { MatchDialog } from "./MatchDialog.tsx";

const OUTCOME: Record<MatchOutcome, { label: string; color: "success" | "error" | "default" }> = {
    WIN: { label: "V", color: "success" },
    LOSS: { label: "P", color: "error" },
    DRAW: { label: "N", color: "default" },
};

interface Props {
    /** precedenti con l'avversario di questa partita */
    eventId?: number;
    /** oppure con questa squadra partecipante */
    teamId?: number;
}

/** Precedenti con una squadra in tutte le stagioni (anche con i nomi precedenti); ogni partita apre il dettaglio */
export default function HeadToHeadPanel({ eventId, teamId }: Props) {
    const [data, setData] = useState<HeadToHead | null>(null);
    const [selected, setSelected] = useState<{ matchId: number; eventId: number } | null>(null);

    useEffect(() => {
        setData(null);
        let alive = true;
        const load = eventId != null ? calendarService.getHeadToHead(eventId)
            : teamId != null ? participantService.getTeamHeadToHead(teamId) : Promise.resolve(null);
        load.then(d => { if (alive) setData(d); }).catch(() => {});
        return () => { alive = false; };
    }, [eventId, teamId]);

    if (!data) return null;
    const formerNames = data.names.slice(1);

    return (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <HistoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Precedenti</Typography>
                {data.matches.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {data.won} vinte · {data.lost} perse{data.drawn ? ` · ${data.drawn} pari` : ""}
                    </Typography>
                )}
            </Stack>
            {formerNames.length > 0 && (
                <Typography variant="caption" color="text.secondary" component="div">
                    In passato anche: {formerNames.join(", ")}
                </Typography>
            )}
            {data.matches.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Nessun incontro precedente
                </Typography>
            ) : (
                <List dense disablePadding>
                    {data.matches.map(m => {
                        const o = m.outcome ? OUTCOME[m.outcome] : null;
                        return (
                            <ListItemButton key={m.matchId} sx={{ px: 0.5, borderRadius: 1 }}
                                onClick={() => setSelected({ matchId: m.matchId, eventId: m.eventId })}>
                                <ListItemText
                                    primary={`${new Date(m.date).toLocaleDateString("it-IT")} · ${m.homeGame ? "in casa" : "in trasferta"} vs ${m.opponentName}`}
                                    secondary={`${m.seasonCode} · ${m.competition}`}
                                />
                                {m.result && <Typography variant="body2" sx={{ fontWeight: 600, mx: 1 }}>{m.result}</Typography>}
                                {o && <Chip size="small" label={o.label} color={o.color} sx={{ minWidth: 32 }} />}
                            </ListItemButton>
                        );
                    })}
                </List>
            )}
            {selected && (
                <MatchDialog open matchId={selected.matchId} eventId={selected.eventId} onClose={() => setSelected(null)} />
            )}
        </Box>
    );
}
